import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'

export interface EmptyStateProps {
  readonly title: string
  readonly description?: string
  readonly action?: ReactNode
  readonly icon?: ReactNode
  /**
   * `muted` — the listing's grey rounded tile. `primary` — a brand-tinted
   * circle, as the Reports pre-result states draw it.
   */
  readonly iconTone?: 'muted' | 'primary'
  readonly className?: string
}

/**
 * Empty / no-results state, per the listing design: a rounded muted tile
 * holding the icon, the title, a muted explanation, then the recovery action.
 */
export function EmptyState({
  title,
  description,
  action,
  icon,
  iconTone = 'muted',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center px-6 py-16 text-center', className)}
    >
      {icon && (
        <div
          className={cn(
            'mb-4 flex size-12 items-center justify-center',
            iconTone === 'primary'
              ? 'rounded-full bg-primary-subtle text-primary'
              : 'rounded-xl bg-surface-muted text-fg-muted',
          )}
        >
          {icon}
        </div>
      )}
      <p className="text-base font-semibold text-fg">{title}</p>
      {description && <p className="mt-1 max-w-md text-sm text-fg-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}
